import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { RadioGroup, Radio } from '@this/shared/ui/inputs/radio';
import { Datepicker } from '@this/shared/ui/inputs/datepicker';
import AutoCompletableInput from '@this/components/shared/autocompletable_input/autocompletable_input';

export type TransportTripType = 'one-way' | 'round-trip';

interface Props {
  tripType: TransportTripType;
  needsHotel: boolean;
  outwardDateTime: Date | null;
  departureLocation: string;
  arrivalLocation: string;
  returnDateTime: Date | null;
  onHotelChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onOutwardDateTimeChange: (date: Date | null) => void;
  onReturnDateTimeChange: (date: Date | null) => void;
  onPlaceChange: (field: 'origin' | 'destination', value: string) => void;
}

const TemplateAssistantTransportFields = ({
  tripType,
  needsHotel,
  outwardDateTime,
  departureLocation,
  arrivalLocation,
  returnDateTime,
  onHotelChange,
  onOutwardDateTimeChange,
  onReturnDateTimeChange,
  onPlaceChange
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  return (
    <>
      <div style={styles.formGroup}>
        <label style={styles.label}>ホテル</label>
        <RadioGroup layout="horizontal" value={needsHotel ? 'yes' : 'no'} onChange={onHotelChange}>
          <Radio value="no">不要</Radio>
          <Radio value="yes">必要</Radio>
        </RadioGroup>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>往路</label>
        <Datepicker
          type="datetime-local"
          value={outwardDateTime ?? undefined}
          onChange={onOutwardDateTimeChange}
          min={new Date()}
          dateFormat="YYYY/MM/DD (ddd) HH:mm"
          disabledClear
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>出発地</label>
        <div style={styles.inputWrapper}>
          <AutoCompletableInput
            id="origin"
            value={departureLocation}
            onChange={onPlaceChange}
            placeholder="会社名 / ビル名 / 駅名など"
          />
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>到着地</label>
        <div style={styles.inputWrapper}>
          <AutoCompletableInput
            id="destination"
            value={arrivalLocation}
            onChange={onPlaceChange}
            placeholder="会社名 / ビル名 / 駅名など"
          />
        </div>
      </div>

      {tripType === 'round-trip' && (
        <div style={styles.formGroup}>
          <label style={styles.label}>復路</label>
          <Datepicker
            type="datetime-local"
            value={returnDateTime ?? undefined}
            onChange={onReturnDateTimeChange}
            min={outwardDateTime ?? new Date()}
            dateFormat="YYYY/MM/DD (ddd) HH:mm"
            disabledClear
          />
        </div>
      )}
    </>
  );
};

const getStyles = (theme: AITTheme) => ({
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: 600 as const,
    color: theme.textColor
  },
  inputWrapper: {
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '4px',
    padding: '8px 12px',
    backgroundColor: theme.whiteColor
  }
});

export default TemplateAssistantTransportFields;
